
import React from 'react';
import { Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import logo from '../../../src/assets/Images/Clanbridge-logo.png2.png';

const PrintCharges = React.forwardRef(({ DataObj, contentRef, otherChargesList, totalChargesAmount, checkoutSettlementData }, ref) => {
  
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB');
  };

  return (
    <Box 
      ref={contentRef} 
      sx={{ 
        padding: 3, 
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        maxWidth: '210mm',
        margin: '0 auto',
        backgroundColor: 'white',
        '@media print': {
          padding: '15px',
          '& *': {
            color: '#000 !important',
          },
          '& .no-print': {
            display: 'none !important',
          }
        }
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          mb: 3,
          borderBottom: '2px solid #635bff',
          pb: 2
        }}
      >
        <Box>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              color: '#635bff', 
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 0.5
            }}
          >
            Clanbridge Living Pvt Ltd
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#666',
              fontWeight: '600',
              fontSize: '1.1rem'
            }}
          >
            Checkout Settlement Charges
          </Typography>
        </Box>
        <img 
          src={logo} 
          alt="Clanbridge Logo"
          style={{ 
            height: '80px', 
            width: 'auto', 
            maxWidth: '120px',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Booking Information */}
      <Box 
        sx={{ 
          mb: 4,
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            backgroundColor: '#635bff',
            color: 'white',
            p: 1.5,
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}
        >
          📋 Booking Details
        </Typography>
        <Box 
          sx={{ 
            p: 2,
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 2,
            backgroundColor: '#fafafa'
          }}
        >
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Booking No:</strong> {DataObj.BookNo || 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Booking Date:</strong> {formatDate(DataObj.BookDt)}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Resident Name:</strong> {DataObj.ResidentName || 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Mobile No:</strong> {DataObj.MobileNo || 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Property:</strong> {DataObj.PropName || 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Settlement No:</strong> {checkoutSettlementData?.ChkOutStlNo || 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Checkout Date:</strong> {DataObj.CheckedOutDt || 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem' }}>
            <strong style={{ color: '#635bff' }}>Settlement Date:</strong> {DataObj.ChkOutStlDt || 'N/A'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            backgroundColor: '#635bff',
            color: 'white',
            p: 1.5,
            borderRadius: '8px 8px 0 0',
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}
        >
          💰 Charges & Refunds
        </Typography>
        <TableContainer 
          component={Paper} 
          sx={{ 
            border: '1px solid #e0e0e0',
            borderTop: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '@media print': {
              boxShadow: 'none',
              border: '1px solid #000'
            }
          }}
        >
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', borderRight: '1px solid #e0e0e0' }}>
                  Charge/Refund Name
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', borderRight: '1px solid #e0e0e0' }}>
                  Amount (₹)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', borderRight: '1px solid #e0e0e0' }}>
                  Discount (₹)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                  Net Amount (₹)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Regular Charges */}
              {otherChargesList
                .filter(charge => !charge.isNegative && (charge.netAmount >= 0 || charge.NetAmt >= 0))
                .map((charge, index) => (
                  <TableRow 
                    key={charge.ChkOutStlDtlId || charge.id || index}
                    sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:last-child td': { borderBottom: 'none' }
                    }}
                  >
                    <TableCell sx={{ borderRight: '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                      {charge.ChrgName || charge.chargeName}
                    </TableCell>
                    <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                      {charge.ChrgAmt || charge.amount}
                    </TableCell>
                    <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                      {charge.DiscAmt || charge.discount}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#2e7d32' }}>
                      {Math.abs(charge.NetAmt || charge.netAmount)}
                    </TableCell>
                  </TableRow>
                ))}

     
              {otherChargesList
                .filter(charge => charge.isNegative || (charge.netAmount < 0 || charge.NetAmt < 0))
                .map((charge, index) => (
                  <TableRow 
                    key={charge.ChkOutStlDtlId || charge.id || index} 
                    sx={{ 
                      backgroundColor: '#fff0f0',
                      '&:last-child td': { borderBottom: 'none' }
                    }}
                  >
                    <TableCell sx={{ borderRight: '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                      {charge.ChrgName || charge.chargeName} (Refund)
                    </TableCell>
                    <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                      {charge.ChrgAmt || charge.amount}
                    </TableCell>
                    <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                      {charge.DiscAmt || charge.discount}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#d32f2f' }}>
                      - {Math.abs(charge.NetAmt || charge.netAmount)}
                    </TableCell>
                  </TableRow>
                ))}

              {/* Total Row */}
              <TableRow sx={{ 
                backgroundColor: '#f8f9fa', 
                borderTop: '2px solid #635bff',
                '& td': { 
                  borderTop: '2px solid #635bff',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }
              }}>
                <TableCell colSpan={3} align="right" sx={{ borderRight: '1px solid #e0e0e0' }}>
                  NET TOTAL:
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    fontSize: '1.1rem',
                    color: totalChargesAmount < 0 ? '#d32f2f' : '#2e7d32'
                  }}
                >
                  {totalChargesAmount < 0 ? '- ' : ''}₹{Math.abs(totalChargesAmount)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>


      <Box 
        sx={{ 
          mt: 3, 
          p: 3, 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          '@media print': {
            backgroundColor: '#f5f5f5 !important',
          }
        }}
      >
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            color: '#635bff',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            mb: 2
          }}
        >
          📊 Summary
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: '600' }}>
            Total {totalChargesAmount < 0 ? 'Refund Amount' : 'Payable Amount'}:
          </Typography>
          <Typography 
            sx={{ 
              color: totalChargesAmount < 0 ? '#d32f2f' : '#2e7d32',
              fontWeight: 'bold',
              fontSize: '1.3rem'
            }}
          >
            {totalChargesAmount < 0 ? '- ' : ''}₹{Math.abs(totalChargesAmount)}
          </Typography>
        </Box>
        {DataObj.Remark && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #ccc' }}>
            <Typography sx={{ fontWeight: '600', mb: 1, color: '#635bff' }}>
              Remarks:
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontStyle: 'italic' }}>
              {DataObj.Remark}
            </Typography>
          </Box>
        )}
      </Box>
      <Box 
        sx={{ 
          mt: 4, 
          textAlign: 'center', 
          borderTop: '2px solid #635bff', 
          pt: 2,
          color: '#666',
          fontSize: '0.9rem'
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          Thank you for choosing Clanbridge Living Pvt Ltd
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          For any queries, please contact: support@clanbridgeliving.in
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          Printed on: {new Date().toLocaleDateString('en-GB')} at {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>
    </Box>
  );
});

PrintCharges.displayName = 'PrintCharges';

export default PrintCharges;